import { ReactElement } from "react";
import { TruncatedAccount, IconButton } from "@narmi/design_system";
import { type ReactLocalization } from "@fluent/react";
import { useClipboard } from "cerulean";
import Account from "byzantine/src/Account";
import Address from "../../settings/profile_settings/UserSettingsProfilePage/Address";
import { PaymentMetadataProps } from "./PaymentActivityDrawer";

const COPIED_MESSAGE = "Copied!";

const CopyableValue = ({ value }: { value: string }) => {
  const { copy, status } = useClipboard(value, {
    successMessage: COPIED_MESSAGE,
  });
  const isCopied = status === COPIED_MESSAGE;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "20ch",
        }}
      >
        {value}
      </span>
      <IconButton
        kind="plain"
        name={isCopied ? "check" : "copy"}
        onClick={copy}
      />
    </span>
  );
};

type PaymentTypeDrawerConfigProps = {
  paymentDetailsRows: (
    metadata: PaymentMetadataProps,
    institutionAccount: Account | null,
    payment: API.FormattedPaymentActivity
  ) => Array<[string, string | undefined | ReactElement<any>]>;
  recipientDetailsRows: (
    recipient: API.Recipient | undefined,
    payment: API.FormattedPaymentActivity,
    metadata: PaymentMetadataProps
  ) => Array<[string, string | undefined | ReactElement<any>]>;
};

const getPaymentTypeDrawerConfig = ({
  l10n,
  institutionDetails,
  renameFields,
  correspondentBankName,
}: {
  l10n: ReactLocalization;
  institutionDetails: { [key: string]: string } | null;
  renameFields: { [key: string]: string };
  correspondentBankName: string | null;
}): Record<string, PaymentTypeDrawerConfigProps> => {
  const getAddressLines = (
    details: API.PaymentActivityDestinationDetails | undefined,
    metadata: PaymentMetadataProps
  ) =>
    [
      details?.beneficiary_address_1 ?? metadata.beneficiary_address_1,
      details?.beneficiary_address_2 ?? metadata.beneficiary_address_2,
      details?.beneficiary_address_3 ?? metadata.beneficiary_address_3,
    ]
      .filter((line) => line)
      .join("\n");

  const truncatedAccountEl = (institutionAccount: Account | null) => (
    <TruncatedAccount
      name={institutionAccount?.name ?? ""}
      lastFour={institutionAccount?.getMaskedNumber() ?? ""}
    />
  );

  const achPaymentConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount) => [
      [l10n.getString("label-account"), truncatedAccountEl(institutionAccount)],
      [
        l10n.getString("ach-payment-transaction-title"),
        metadata.std_ent_cls_code
          ? l10n.getString("label-std-code-payment", {
              code: metadata.std_ent_cls_code,
            })
          : undefined,
      ],
      [l10n.getString("label-company-name"), metadata.company_name],
      [l10n.getString("label-company-id"), metadata.company_id],
      [l10n.getString("label-entry-description"), metadata.entry_desc],
      [l10n.getString("label-memo"), metadata.description],
      [l10n.getString("label-payment-id"), metadata.payment_identification],
      [l10n.getString("label-note-internal"), metadata.memo],
    ],
    recipientDetailsRows: (recipient, payment) => {
      const details = payment.destinationDetails;
      const achDestination = recipient?.ach_destination;
      const name = details?.beneficiary_name || recipient?.name;
      const bank =
        details?.institution_name || achDestination?.institution_name;
      const account = details?.account_number || achDestination?.account_number;
      const routing = details?.routing_number || achDestination?.routing_number;

      return [
        [l10n.getString("label-name"), name],
        [l10n.getString("ach-payment-review-bank"), bank],
        [l10n.getString("ach-payment-review-account"), account],
        [l10n.getString("ach-payment-review-routing"), routing],
      ];
    },
  };

  const wireTransactionConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount) => {
      const memo = [
        metadata.memo_1,
        metadata.memo_2,
        metadata.memo_3,
        metadata.memo_4,
      ]
        .filter((line) => line)
        .join("\n");
      return [
        [
          l10n.getString("label-account"),
          truncatedAccountEl(institutionAccount),
        ],
        [l10n.getString("label-wire-reason"), metadata.wire_reason],
        [l10n.getString("label-memo"), memo],
      ];
    },
    recipientDetailsRows: (recipient, payment, metadata) => {
      const details = payment.destinationDetails;
      const fedwireDestination = recipient?.fedwire_destination;
      const address = getAddressLines(details, metadata);
      const name = details?.beneficiary_name || recipient?.name;
      const bank =
        details?.institution_name || fedwireDestination?.institution_name;
      const account =
        details?.account_number || fedwireDestination?.account_number;
      const routing =
        details?.routing_number || fedwireDestination?.routing_number;

      return [
        [l10n.getString("label-name"), name],
        [l10n.getString("ach-payment-review-bank"), bank],
        [l10n.getString("ach-payment-review-account"), account],
        [l10n.getString("ach-payment-review-routing"), routing],
        [l10n.getString("label-address"), address],
      ];
    },
  };

  const swiftWireTransactionConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount) => [
      [l10n.getString("label-account"), truncatedAccountEl(institutionAccount)],
      [l10n.getString("label-wire-reason"), metadata.wire_reason],
      [l10n.getString("label-memo"), metadata.memo],
      [l10n.getString("label-international-wire-review-note"), metadata.note],
      [
        l10n.getString("label-transaction-id"),
        metadata.payment_identification ? (
          <CopyableValue value={metadata.payment_identification} />
        ) : undefined,
      ],
    ],
    recipientDetailsRows: (recipient, payment) => {
      const details = payment.destinationDetails;
      const swiftDestination = recipient?.swift_destination;
      const bic = details?.bic || swiftDestination?.bic;
      const accountIdentifier =
        details?.account_identifier || swiftDestination?.account_identifier;
      const address = details?.address || swiftDestination?.address;
      const isAccountNumber =
        renameFields?.["account_identifier"] === "Account number" ||
        renameFields?.["account_identifier"] === "IBAN";

      return [
        [
          l10n.getString("label-name"),
          details?.beneficiary_name || recipient?.name,
        ],
        [
          l10n.getString("label-address"),
          address ? <Address address={address as API.Address} /> : "",
        ],
        [
          l10n.getString("ach-payment-review-bank"),
          institutionDetails?.institutionName,
        ],
        [
          l10n.getString("label-international-wire-beneficiary-bank"),
          swiftDestination?.metadata?.bank_name,
        ],
        [l10n.getString("swift-bic"), bic],
        [
          l10n.getString("ach-payment-review-account"),
          isAccountNumber ? accountIdentifier : "",
        ],
        [
          l10n.getString("label-international-wire-review-correspondent-bank"),
          correspondentBankName ?? undefined,
        ],
      ];
    },
  };

  const scheduledSwiftWireTransferConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount) => [
      [l10n.getString("label-account"), truncatedAccountEl(institutionAccount)],
      [l10n.getString("label-wire-reason"), metadata.wire_reason],
      [l10n.getString("label-memo"), metadata.memo],
      [l10n.getString("label-international-wire-review-note"), metadata.note],
    ],
    recipientDetailsRows: swiftWireTransactionConfig.recipientDetailsRows,
  };

  const fedNowTransactionConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount) => [
      [l10n.getString("label-account"), truncatedAccountEl(institutionAccount)],
      [l10n.getString("label-payment-id"), metadata.payment_identification],
      [l10n.getString("label-description"), metadata.memo],
    ],
    recipientDetailsRows: (recipient, payment) => {
      const details = payment.destinationDetails;
      const achDestination = recipient?.ach_destination;
      const name = details?.beneficiary_name || recipient?.name;
      const bank =
        details?.institution_name || achDestination?.institution_name;
      const account = details?.account_number || achDestination?.account_number;
      const routing = details?.routing_number || achDestination?.routing_number;

      return [
        [l10n.getString("label-name"), name],
        [l10n.getString("ach-payment-review-bank"), bank],
        [l10n.getString("ach-payment-review-account"), account],
        [l10n.getString("ach-payment-review-routing"), routing],
      ];
    },
  };

  // a reversal has no payment details of its own beyond amount/description
  // the company/SEC/entry-description fields live on the original ACH payment,
  // whose metadata is fetched and passed in here
  // Account/Amount/Memo come from the reversal itself
  // while the remaining fields come from the original payment's metadata
  const reversalConfig: PaymentTypeDrawerConfigProps = {
    paymentDetailsRows: (metadata, institutionAccount, payment) => [
      [l10n.getString("label-account"), truncatedAccountEl(institutionAccount)],
      [l10n.getString("label-amount"), payment.positiveDisplayAmountDollars],
      [
        l10n.getString("ach-payment-transaction-title"),
        metadata.std_ent_cls_code
          ? l10n.getString("label-std-code-payment", {
              code: metadata.std_ent_cls_code,
            })
          : undefined,
      ],
      [l10n.getString("label-company-name"), metadata.company_name],
      [l10n.getString("label-company-id"), metadata.company_id],
      [l10n.getString("label-entry-description"), metadata.entry_desc],
      [l10n.getString("label-memo"), payment.description],
      [l10n.getString("label-payment-id"), metadata.payment_identification],
      [l10n.getString("label-note-internal"), metadata.memo],
    ],
    recipientDetailsRows: achPaymentConfig.recipientDetailsRows,
  };

  return {
    UnverifiedACHTransaction: achPaymentConfig,

    UnverifiedACHTransfer: achPaymentConfig,

    UnverifiedACHReversal: reversalConfig,

    WireTransaction: wireTransactionConfig,

    SwiftWireTransaction: swiftWireTransactionConfig,

    SwiftWireTransfer: scheduledSwiftWireTransferConfig,

    FedNowTransaction: fedNowTransactionConfig,
  };
};

export default getPaymentTypeDrawerConfig;
